import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Reads the current parent's consent_logs and computes the active
 * consent state per category. The consent_logs table is append-only
 * (UK-GDPR Art 7) — every grant or revoke is a new row. The latest
 * row per `consent_type` wins; `revoked_at IS NULL` means active.
 *
 * Wave 3.3 commit-parental-consent writes consent_logs with
 * `user_id = parent's auth uid`. Wave 3.5 will let parents revoke
 * via Settings → Privacy by appending a new row with revoked_at set.
 *
 * Surfaced on the parent's child detail screens (Highlights gates on
 * MEDIA, AiNote gates on AI_DEVELOPMENT_ANALYSIS).
 */

export interface ParentConsentState {
  /** Active = the parent ticked + has not revoked. */
  isActive: (consentType: string) => boolean;
  /** Latest grant timestamp per consent type, ISO. Null if never granted. */
  lastGrantedAt: (consentType: string) => string | null;
}

interface State {
  data: ParentConsentState;
  loading: boolean;
  error: string | null;
  /** Map of consent_type -> latest row, exposed for fine-grained UI. */
  rawByType: Record<string, { grantedAt: string; revokedAt: string | null }>;
}

const EMPTY_STATE: ParentConsentState = {
  isActive: () => false,
  lastGrantedAt: () => null,
};

export function useParentConsents(): State {
  const { user, bypass } = useAuth();
  const [state, setState] = useState<State>({
    data: EMPTY_STATE,
    loading: true,
    error: null,
    rawByType: {},
  });

  useEffect(() => {
    let cancelled = false;
    if (!user || bypass) {
      setState({ data: EMPTY_STATE, loading: false, error: null, rawByType: {} });
      return;
    }
    setState((s) => ({ ...s, loading: true }));

    (async () => {
      const { data, error } = await supabase
        .from('consent_logs')
        .select('consent_type, granted_at, revoked_at')
        .eq('user_id', user.id)
        .order('granted_at', { ascending: false })
        .limit(200);
      if (cancelled) return;
      if (error) {
        setState({ data: EMPTY_STATE, loading: false, error: error.message, rawByType: {} });
        return;
      }

      const latest: Record<string, { grantedAt: string; revokedAt: string | null }> = {};
      for (const row of data ?? []) {
        const t = String((row as any).consent_type ?? '');
        if (!t) continue;
        // Order is desc by granted_at — first occurrence per type is the latest.
        if (!(t in latest)) {
          latest[t] = {
            grantedAt: String((row as any).granted_at ?? ''),
            revokedAt: ((row as any).revoked_at as string | null) ?? null,
          };
        }
      }

      const consentState: ParentConsentState = {
        isActive: (t) => {
          const row = latest[t];
          return Boolean(row && row.revokedAt === null);
        },
        lastGrantedAt: (t) => latest[t]?.grantedAt ?? null,
      };

      setState({ data: consentState, loading: false, error: null, rawByType: latest });
    })();

    return () => { cancelled = true; };
  }, [user, bypass]);

  return state;
}
